import React, { useEffect, useState } from 'react';

import MuxPlayer from '@mux/mux-player-react';

import { makePlaceholderDataUri, safeDimension } from '../../lib/attachments';

import { computeImageFrameLayout, resolveVideoIntrinsicDimensions } from '../../feed/echo/attachmentGeometry';

const VideoAttachment = ({ attachment, mode = 'single' }) => {
  const muxVariant = attachment.variants?.mux;
  const playbackId = muxVariant?.playback_id;
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    setVideoLoaded(false);
  }, [playbackId]);

  // The mux variant only exists after Mux processing completes; the frame must be
  // sized from poster/upload metadata before that so the player arriving later
  // (webhook update or a stale warm-cache row refreshing) never resizes the row.
  const { width: intrinsicWidth, height: intrinsicHeight } = resolveVideoIntrinsicDimensions(attachment);
  const posterUrl = attachment.variants?.large?.public_url || attachment.variants?.medium?.public_url;
  const layout = computeImageFrameLayout(intrinsicWidth, intrinsicHeight, mode);
  const { finalFrameW, finalFrameH, frameClass, frameStyle, variant } = layout;

  const placeholderWidth = safeDimension(finalFrameW ?? intrinsicWidth, intrinsicWidth);
  const placeholderHeight = safeDimension(finalFrameH ?? intrinsicHeight, intrinsicHeight);
  const placeholderSrc = makePlaceholderDataUri(placeholderWidth, placeholderHeight);
  const stableFrameWidth = finalFrameW ?? placeholderWidth;
  const isSingleRegular = variant === 'single:regular';
  const isScrollerRegular = variant?.startsWith('scroller:');

  const divStyle = {};
  if (isSingleRegular) {
    // Definite px width with a 100% cap — mirrors ImageFrame: percentage widths make
    // the intrinsic contribution balloon past the frame, so w-fit wrappers stop hugging.
    divStyle.width = stableFrameWidth;
    divStyle.maxWidth = '100%';
  } else if (isScrollerRegular) {
    divStyle.width = stableFrameWidth;
  } else {
    divStyle.width = stableFrameWidth;
    divStyle.maxWidth = '100%';
  }

  const baseFigureStyle = { ...frameStyle };
  if (!isSingleRegular && !isScrollerRegular) {
    baseFigureStyle.width = stableFrameWidth;
    baseFigureStyle.maxWidth = '100%';
  }

  const objectFitStyle = layout.imgStyle?.objectFit || (layout.imgClass?.includes('object-cover') ? 'cover' : 'contain');

  const handleLoadedMetadata = () => {
    setVideoLoaded(true);
  };

  return (
    <div className={`inline-flex flex-col gap-1 ${isScrollerRegular ? 'shrink-0' : ''}`} style={divStyle}>
      <figure
        className={`group/video relative overflow-hidden rounded-2xl ${frameClass}`}
        style={baseFigureStyle}
        data-variant={`${variant}:video`}
      >
        <img
          src={placeholderSrc}
          alt=""
          aria-hidden="true"
          className={`${layout.imgClass} pointer-events-none transition-opacity duration-200 select-none ${
            videoLoaded ? 'opacity-0' : 'opacity-50'
          }`}
          style={layout.imgStyle}
          width={placeholderWidth}
          height={placeholderHeight}
        />

        {posterUrl && !videoLoaded && (
          <img
            src={posterUrl}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 size-full select-none"
            style={{ objectFit: objectFitStyle }}
          />
        )}

        {playbackId ? (
          <MuxPlayer
            playbackId={playbackId}
            poster={posterUrl}
            placeholder={posterUrl}
            defaultDuration={muxVariant?.duration}
            className={`absolute inset-0 size-full cursor-pointer overflow-hidden rounded-2xl transition-opacity duration-200 ${
              videoLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ width: '100%', height: '100%', objectFit: objectFitStyle }}
            onLoadedMetadata={handleLoadedMetadata}
          />
        ) : (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-neutral-950/60 px-3 py-1.5 text-2xs font-medium text-white select-none">
              Processing video…
            </span>
          </div>
        )}
      </figure>
    </div>
  );
};

export default VideoAttachment;
